// Rhemn iOS native-app bridge (JS side).
//
// Implements the JS half of plans/ios-native-bridge-protocol.md v1 EXACTLY —
// the Swift half (NativeBridge.swift) is built against the same document.
//
// Detection: the iOS host injects `window.__RHEMN_NATIVE__` via a WKUserScript
// at document start. When that object is absent (Chrome, Safari extension,
// plain web) every export here is dead code: isNativeApp() is false and
// bridge.call rejects immediately with UNAVAILABLE. Never sniff the user agent.

export type NativePlatform = 'ios' | 'ipados';

export interface RhemnNativeInfo {
  platform: NativePlatform;
  osVersion: string;
  appVersion: string;
  build: string;
  bridgeVersion: number;
  /** Contract F9: absent on older builds → copy falls back to "device". */
  idiom?: 'phone' | 'pad' | 'mac';
  /** v1.4: chrome pieces the host draws natively in iOS 26 Liquid Glass
   *  (absent before iOS 26 and on older builds → the CSS chrome stays). */
  nativeChrome?: NativeChromePiece[];
}

export type NativeChromePiece = 'tabBar' | 'header' | 'cards' | 'weatherSheet' | 'insightsSheet';

// PRODUCTS_UNAVAILABLE (protocol v1.2, iap.* only): the App Store product
// catalog could not be fetched or lacks the requested product — surfaced as a
// calm "App Store unreachable" message, distinct from a generic failure.
export type BridgeErrorCode = 'CANCELLED' | 'DENIED' | 'UNAVAILABLE' | 'FAILED' | 'PRODUCTS_UNAVAILABLE';

/** Error rejected by bridge.call — carries the protocol error code. */
export class BridgeError extends Error {
  readonly code: BridgeErrorCode;

  constructor(code: BridgeErrorCode, message: string) {
    super(message);
    this.name = 'BridgeError';
    this.code = code;
  }
}

/** Protocol error code of a rejected bridge.call error, or null for other errors. */
export const bridgeErrorCode = (err: unknown): BridgeErrorCode | null =>
  err instanceof BridgeError ? err.code : null;

// Reply envelope (protocol §3) — the native side never throws across the
// boundary; every reply is one of these two shapes.
interface BridgeReplyOk {
  ok: true;
  data: unknown;
}
interface BridgeReplyErr {
  ok: false;
  error: { code: BridgeErrorCode; message: string };
}
type BridgeReply = BridgeReplyOk | BridgeReplyErr;

interface RhemnBridgeHandler {
  // WKScriptMessageHandlerWithReply — postMessage returns a promise (iOS 14+).
  postMessage(message: { method: string; params: Record<string, unknown> }): Promise<unknown>;
}

const getNativeInfo = (): RhemnNativeInfo | null => {
  if (typeof window === 'undefined') return null;
  const info = (window as { __RHEMN_NATIVE__?: unknown }).__RHEMN_NATIVE__;
  // `typeof null === 'object'` — guard it explicitly.
  return typeof info === 'object' && info !== null ? (info as RhemnNativeInfo) : null;
};

/** True when running inside the iOS/iPadOS native app's WKWebView. */
export const isNativeApp = (): boolean => getNativeInfo() !== null;

/** 'ios' | 'ipados' when native, null everywhere else. */
export const nativePlatform = (): NativePlatform | null => getNativeInfo()?.platform ?? null;

/** The full host-injected info object (version footer etc.), null off-native. */
export const nativeAppInfo = (): RhemnNativeInfo | null => getNativeInfo();

/** v1.4: this web view is an embedded surface the host presents natively
 *  ('insights' = the Insights system sheet's second web view), else null. */
export const nativeEmbed = (): 'insights' | null => {
  if (typeof window === 'undefined') return null;
  return (window as { __RHEMN_EMBED__?: unknown }).__RHEMN_EMBED__ === 'insights' ? 'insights' : null;
};

/** v1.4: the host draws this chrome piece natively (iOS 26 Liquid Glass). */
export const nativeChromeSupports = (piece: NativeChromePiece): boolean =>
  getNativeInfo()?.nativeChrome?.includes(piece) ?? false;

const getBridgeHandler = (): RhemnBridgeHandler | null => {
  if (typeof window === 'undefined') return null;
  const handler = (window as {
    webkit?: { messageHandlers?: { rhemnBridge?: RhemnBridgeHandler } };
  }).webkit?.messageHandlers?.rhemnBridge;
  return typeof handler?.postMessage === 'function' ? handler : null;
};

// JS-side timeout (protocol §3): a hung native handler must never wedge the UI.
// Interactive methods present a system sheet/prompt the user can sit under
// (Ask to Buy deliberation, sign-in sheets, permission prompts) — a flat 30s
// would cancel a purchase mid-thought. Everything else is programmatic and
// keeps the tight 30s hang guard.
const CALL_TIMEOUT_MS = 30_000;
const INTERACTIVE_CALL_TIMEOUT_MS = 300_000; // 5 min
const INTERACTIVE_METHODS: ReadonlySet<string> = new Set([
  'auth.signInApple',
  'auth.signInGoogle',
  'iap.purchase',
  'iap.restore',
  'iap.redeemCode',
  'iap.manageSubscriptions',
  'notifications.requestPermission',
  'location.get',
  // Contract §4: ASWebAuthenticationSession for Calendars & tasks OAuth.
  'integrations.openAuth',
  // Contract §4: the EventKit permission prompts.
  'calendar.requestAccess',
  'reminders.requestAccess',
]);

const timeoutMsFor = (method: string): number =>
  INTERACTIVE_METHODS.has(method) ? INTERACTIVE_CALL_TIMEOUT_MS : CALL_TIMEOUT_MS;

const isBridgeReply = (value: unknown): value is BridgeReply => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { ok?: unknown; error?: unknown };
  if (v.ok === true) return true;
  return v.ok === false && typeof v.error === 'object' && v.error !== null;
};

/**
 * Promise RPC to the native host (protocol §3).
 * Resolves with the reply's `data`; rejects with a BridgeError carrying `.code`.
 * Off-native (or if the handler is missing) rejects UNAVAILABLE immediately.
 */
const call = <T>(method: string, params?: Record<string, unknown>): Promise<T> => {
  const handler = getBridgeHandler();
  if (!isNativeApp() || !handler) {
    return Promise.reject(new BridgeError('UNAVAILABLE', `native bridge unavailable for ${method}`));
  }

  const timeoutMs = timeoutMsFor(method);

  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new BridgeError('UNAVAILABLE', `native bridge timed out after ${timeoutMs}ms for ${method}`));
    }, timeoutMs);

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
    };

    handler
      .postMessage({ method, params: params ?? {} })
      .then((reply) => {
        finish(() => {
          if (!isBridgeReply(reply)) {
            reject(new BridgeError('FAILED', `malformed bridge reply for ${method}`));
            return;
          }
          if (reply.ok) {
            resolve(reply.data as T);
          } else {
            reject(new BridgeError(reply.error.code, reply.error.message));
          }
        });
      })
      .catch((err: unknown) => {
        // The native side replies with an error envelope rather than throwing;
        // a rejected postMessage promise means the handler itself blew up.
        finish(() => {
          reject(new BridgeError('FAILED', err instanceof Error ? err.message : `bridge call failed for ${method}`));
        });
      });
  });
};

export const bridge = { call };

/**
 * Keeps the device screen awake while a long mic recording runs (screen lock
 * kills the WKWebView microphone). Fire-and-forget: resolves silently on
 * non-native hosts and on bridge errors — the recording itself must never
 * fail because a convenience toggle did. Callers MUST call with `false` when
 * the recording ends; the iOS host also resets the flag on backgrounding.
 */
export const requestKeepAwake = async (on: boolean): Promise<void> => {
  if (!isNativeApp()) return;
  try {
    await call('ui.keepAwake', { on });
  } catch {
    /* UNAVAILABLE on older app builds — recording proceeds without it */
  }
};

// ── JS-side shell requests (NOT part of the Swift protocol) ──────────────────

/**
 * Window event asking the native shell to select the Home tab. Dispatched by
 * shared surfaces that cannot know about the shell's tab state: FocusSession's
 * session strip tap, and every native session close ("exiting the session
 * lands on Home facing the PrayerCard" — design spec §12.5). JS↔JS only —
 * never crosses the bridge. Dispatch is a no-op off-native; nothing listens.
 */
export const GO_HOME_EVENT = 'rhemn-go-home';

export const requestNativeHome = (): void => {
  if (!isNativeApp()) return;
  window.dispatchEvent(new Event(GO_HOME_EVENT));
};

// ── Native → JS events (protocol §4) ─────────────────────────────────────────

// 'eventkit.changed' (contract §4): payload {}, debounced 1 s from
// EKEventStoreChanged; also fires after Rhemn's own EventKit writes.
// 'chrome.tap' (v1.4): payload { target: 'tab:<key>' | 'weather' | 'qibla' |
// 'streak' } — a native Liquid Glass control was tapped; JS clicks its ghost.
// 'chrome.sheet' (v1.4): payload { id, action, value? } from a native sheet.
// 'insights.show' / 'insights.hide' (v1.4, embed only): payload { verse } /
// {} — the Insights system sheet presented / dismissed this embed.
export type NativeEventType =
  | 'refresh' | 'purchase.updated' | 'app.becameActive' | 'navigate' | 'eventkit.changed'
  | 'chrome.tap' | 'chrome.sheet' | 'insights.show' | 'insights.hide';

const NATIVE_EVENT_NAME = 'rhemn-native';

/**
 * Dispatch a `rhemn-native` event locally, with the exact envelope the Swift
 * host uses (protocol §4) — so JS-originated occurrences of a protocol event
 * (e.g. nativeIAP dispatching purchase.updated after a JS-initiated purchase)
 * converge on the same onNativeEvent listeners as host-emitted ones.
 */
export const emitNativeEvent = (type: NativeEventType, payload: unknown = {}): void => {
  window.dispatchEvent(new CustomEvent(NATIVE_EVENT_NAME, { detail: { type, payload } }));
};

/**
 * Subscribe to a native-dispatched `rhemn-native` CustomEvent of the given type.
 * Returns an unsubscribe function. Safe to call off-native (the event simply
 * never fires there).
 */
export const onNativeEvent = (
  type: NativeEventType,
  handler: (payload: unknown) => void,
): (() => void) => {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<{ type?: string; payload?: unknown }>).detail;
    if (detail?.type === type) handler(detail.payload);
  };
  window.addEventListener(NATIVE_EVENT_NAME, listener);
  return () => window.removeEventListener(NATIVE_EVENT_NAME, listener);
};
